/**
 * Minimal, dependency-free first-party cookie helpers.
 *
 * The app primarily uses localStorage, but cookies are required for integrations
 * that mandate them (e.g. Reserve with Google conversion tracking). Kept generic
 * so other features can reuse it. All functions are SSR-safe.
 */

interface SetCookieOptions {
  /** Domain scope. Omit for a host-only cookie. */
  domain?: string;
  /** Cookie lifetime in seconds (maps to the `max-age` attribute). */
  maxAgeSeconds?: number;
  /** Path scope. Defaults to `/`. */
  path?: string;
}

export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) {
    return undefined;
  }

  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie.split('; ').find((row) => row.startsWith(prefix));

  if (!match) {
    return undefined;
  }

  return decodeURIComponent(match.slice(prefix.length));
}

export function setCookie(name: string, value: string, options: SetCookieOptions = {}): void {
  if (typeof document === 'undefined') {
    return;
  }

  const { maxAgeSeconds, domain, path = '/' } = options;
  const parts = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`, `path=${path}`];

  if (typeof maxAgeSeconds === 'number') {
    parts.push(`max-age=${maxAgeSeconds}`);
  }
  if (domain) {
    parts.push(`domain=${domain}`);
  }
  parts.push('SameSite=Lax');
  // `Secure` requires https; omit it on localhost/http dev so the cookie still works there.
  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

/**
 * Expires a cookie. `domain`/`path` must match those it was written with — the browser treats
 * a differently scoped cookie as a separate one and the original would survive.
 */
export function deleteCookie(
  name: string,
  options: Pick<SetCookieOptions, 'domain' | 'path'> = {},
): void {
  setCookie(name, '', { ...options, maxAgeSeconds: 0 });
}
